// Install / "Add to Home Screen" prompt. LINEBRKR is already a full PWA (manifest + sw +
// icons), so this just surfaces the install so a new user can get it onto their home
// screen in one tap. Two render variants:
//   - "banner"  : slim dismissible bar, mounted app-wide (snoozes 7 days on dismiss)
//   - "cta"     : a big green button, dropped on the post-signup "You're in" screen
// Android/Chrome/Edge: captures beforeinstallprompt → one-tap native install.
// iOS Safari: no programmatic install exists, so we show the Share → Add to Home Screen steps.
import React, { useState, useEffect } from 'react'

const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
  } catch (_) { return false }
}
const isIOS = () => {
  try { return /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream }
  catch (_) { return false }
}
// iPadOS 13+ reports as desktop Safari; catch it via touch + Mac platform.
const isIPadOS = () => {
  try { return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 }
  catch (_) { return false }
}
const iosLike = () => isIOS() || isIPadOS()

const G = '#00D97E'

function IOSSteps() {
  return (
    <div style={{ marginTop: 10, background: '#0F1520', border: '1px solid #22303f',
      borderRadius: 10, padding: '12px 14px', fontSize: 13, color: '#cfe0d6', lineHeight: 1.6, textAlign: 'left' }}>
      <div style={{ fontWeight: 800, color: '#eaf3ec', marginBottom: 4 }}>Add LINEBRKR to your home screen</div>
      1. Tap the <b style={{ color: '#eaf3ec' }}>Share</b> icon&nbsp;
      <span aria-hidden style={{ display: 'inline-block', transform: 'translateY(2px)' }}>
        <svg width="14" height="16" viewBox="0 0 24 24" fill="none" stroke={G} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v13" /><path d="m7 7 5-5 5 5" /><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" /></svg>
      </span>
      &nbsp;in Safari's toolbar<br />
      2. Scroll down and tap <b style={{ color: '#eaf3ec' }}>Add to Home Screen</b><br />
      3. Tap <b style={{ color: '#eaf3ec' }}>Add</b> — LINEBRKR opens like an app from then on.
    </div>
  )
}

export default function InstallPrompt({ variant = 'banner' }) {
  const [deferred, setDeferred] = useState(null)  // Android beforeinstallprompt event
  const [show, setShow] = useState(false)
  const [iosOpen, setIosOpen] = useState(variant === 'cta')  // CTA shows iOS steps inline

  useEffect(() => {
    if (isStandalone()) return  // already installed → never prompt
    if (variant === 'banner') {
      let snoozed = false
      try { snoozed = (Date.now() - Number(localStorage.getItem('brkr_install_snooze') || 0)) < 7 * 864e5 } catch (_) {}
      if (snoozed) return
    }
    const onBIP = (e) => { e.preventDefault(); setDeferred(e); setShow(true) }
    window.addEventListener('beforeinstallprompt', onBIP)
    if (iosLike()) setShow(true)  // iOS never fires BIP — surface the manual nudge
    // Installed during the session → tidy up.
    const onInstalled = () => setShow(false)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [variant])

  if (isStandalone()) return null
  if (variant === 'banner' && !show) return null

  const doInstall = async () => {
    if (deferred) {
      deferred.prompt()
      try { await deferred.userChoice } catch (_) {}
      setDeferred(null); setShow(false)
    } else {
      setIosOpen(v => !v)  // iOS/unsupported: toggle the Share-sheet steps
    }
  }
  const snooze = () => {
    try { localStorage.setItem('brkr_install_snooze', String(Date.now())) } catch (_) {}
    setShow(false)
  }

  // ── CTA variant: a full-width button for the signup-complete screen ──
  if (variant === 'cta') {
    return (
      <div style={{ marginTop: 14 }}>
        <button onClick={doInstall} style={{ width: '100%', background: G, border: 'none',
          borderRadius: 10, padding: '14px 0', color: '#04140D', fontSize: 15, fontWeight: 800,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          📲 {deferred ? 'Install the app' : 'Add LINEBRKR to your home screen'}
        </button>
        {iosLike() && iosOpen && <IOSSteps />}
        <div style={{ marginTop: 8, fontSize: 11.5, color: '#7f9788', lineHeight: 1.4 }}>
          Opens full-screen like a native app — no App Store needed.
        </div>
      </div>
    )
  }

  // ── Banner variant: slim, dismissible, app-wide ──
  return (
    <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 9000,
      display: 'flex', justifyContent: 'center', padding: '0 10px 10px', pointerEvents: 'none' }}>
      <div style={{ pointerEvents: 'auto', width: '100%', maxWidth: 460, background: '#111827',
        border: `1px solid ${G}`, borderRadius: 14, padding: '12px 14px',
        boxShadow: '0 10px 30px -12px rgba(0,0,0,.8)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22 }}>📲</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: '#F1F5F9' }}>Install LINEBRKR</div>
            <div style={{ fontSize: 11.5, color: '#A6B2C4', marginTop: 1 }}>Add it to your home screen — opens like an app.</div>
          </div>
          <button onClick={doInstall} style={{ flexShrink: 0, background: G, border: 'none',
            borderRadius: 9, padding: '8px 14px', color: '#04140D', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
            {deferred ? 'Install' : iosLike() ? 'How' : 'Install'}
          </button>
          <button onClick={snooze} aria-label="Dismiss" style={{ flexShrink: 0, background: 'none',
            border: 'none', color: '#7f9788', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '4px 2px' }}>×</button>
        </div>
        {iosLike() && iosOpen && <IOSSteps />}
      </div>
    </div>
  )
}
